import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Droplet, Upload, Loader2, AlertCircle,
  Info, Circle, HelpCircle, Sparkles, Crown, Activity, Clock, Shield,
  FileImage, HeartPulse, Camera, Lightbulb, FileText,BookOpen,
  ArrowRight, ShieldCheck, Lock
} from 'lucide-react';
import { toast } from 'sonner';

// --- CONFIGURATION & CONSTANTES ---
const RESULT_COLORS = {
  inRange: { hex: '#0D9488', text: '#0F766E', textDark: '#2DD4BF', icon: Circle },
  outOfRange: { hex: '#0369A1', text: '#075985', textDark: '#38BDF8', icon: Info },
  unreadable: { hex: '#475569', text: '#334155', textDark: '#94A3B8', icon: HelpCircle },
};

// Correspondance balise → catégorie. Les deux vocabulaires cohabitent : le backend produit
// encore l'ancien ([NORMAL]/[SURVEILLER]/[CONSULTATION]), le nouveau est descriptif
// ([DANS_INTERVALLE]/[HORS_INTERVALLE]/[NORME_NON_LISIBLE]). [SURVEILLER] et [CONSULTATION]
// fusionnent : même fait (hors intervalle), ils ne différaient que par une estimation de gravité.
const STATUS_TAGS = {
  '[NORMAL]': 'inRange',
  '[DANS_INTERVALLE]': 'inRange',
  '[SURVEILLER]': 'outOfRange',
  '[CONSULTATION]': 'outOfRange',
  '[HORS_INTERVALLE]': 'outOfRange',
  '[NORME_NON_LISIBLE]': 'unreadable',
};

const functions = getFunctions(undefined, 'europe-west1');

// --- COMPOSANT PRINCIPAL ---
const BloodTestAnalysis = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  // Accès lu depuis le contexte (abonnement OU essai), au lieu du getDoc local.
  // Alias `isPremium` pour garder le reste du composant inchangé.
  const { user, loading: checkingPremium, hasPremiumAccess: isPremium, isTrialExpired } = useAuth();

  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Redirection si non connecté (conservée du getDoc précédent)
  useEffect(() => {
    if (!checkingPremium && !user) navigate('/login');
  }, [checkingPremium, user, navigate]);

  // --- LOGIQUE DE FICHIERS ---
  const handleFile = (file) => {
    if (!file.type.startsWith('image/')) return toast.error('Format image requis');
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setImage(reader.result.split(',')[1]);
      toast.success('Document prêt pour l\'analyse');
    };
    reader.readAsDataURL(file);
  };

  const analyzeImage = async () => {
    if (!image) return;
    setLoading(true);
    try {
      const analyzeBloodTest = httpsCallable(functions, 'analyzeBloodTest');
      const result = await analyzeBloodTest({ imageBase64: image });
      if (result.data?.success) {
        setAnalysis(result.data.analysis);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      toast.error("Erreur d'analyse. Vérifiez la qualité de l'image.");
    } finally {
      setLoading(false);
    }
  };

  // --- PARSING DE L'ANALYSE ---
  const renderAnalysisContent = useMemo(() => {
    if (!analysis) return null;
    // Style d'une ligne à partir de SA BALISE UNIQUEMENT (ancien + nouveau vocabulaire).
    // Aucune heuristique sur le texte libre.
    const getStatusStyle = (line) => {
      for (const [tag, category] of Object.entries(STATUS_TAGS)) {
        if (line.includes(tag)) return RESULT_COLORS[category];
      }
      return null;
    };
    return analysis.split('\n').map((line, i) => {
      const style = getStatusStyle(line);
      if (style) {
        const Icon = style.icon;
        return (
          <div key={i} className="mt-6 p-5 rounded-2xl border bg-white dark:bg-gray-800 shadow-sm border-l-4" style={{ borderLeftColor: style.hex }}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0" style={{ backgroundColor: style.hex }}>
                <Icon className="w-5 h-5" />
              </div>
              <h4 className="text-lg font-bold" style={{ color: theme === 'dark' ? style.textDark : style.text }}>
                {line.replace(/\[.*?\]|\*\*|#/g, '').trim()}
              </h4>
            </div>
          </div>
        );
      }
      return <p key={i} className="text-gray-700 dark:text-gray-300 my-2 leading-relaxed">{line.replace(/\*\*|#/g, '').trim()}</p>;
    });
  }, [analysis, theme]);

  if (checkingPremium) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-500" /></div>;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-12">
        
        {/* ── SECTION 1 : ENTÊTE & SÉCURITÉ ── */}
        <header className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 bg-gradient-to-tr from-red-600 to-rose-400 rounded-3xl flex items-center justify-center shadow-2xl shadow-rose-500/20 rotate-3">
                <Droplet className="w-9 h-9 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-black tracking-tight text-gray-900 dark:text-white">Analyse Intelligente</h1>
                <p className="text-gray-500 font-medium">Comprendre ses prises de sang sans jargon médical.</p>
              </div>
            </div>

            {/* Badge de confiance amélioré */}
            <div className="flex items-center gap-4 p-4 bg-blue-50/50 dark:bg-blue-950/30 rounded-2xl border border-blue-100 dark:border-blue-800">
              <div className="p-2 bg-blue-500 rounded-xl shadow-lg shadow-blue-500/30">
                <ShieldCheck className="text-white w-6 h-6" />
              </div>
              <div>
                <span className="block text-sm font-bold text-blue-900 dark:text-blue-300">Données chiffrées</span>
                <span className="text-xs text-blue-700/70 dark:text-blue-400/70">Conforme normes RGPD santé</span>
              </div>
            </div>
          </div>

          <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border-l-4 border-amber-400 rounded-r-xl flex gap-3 italic text-sm text-amber-800 dark:text-amber-200">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            Cette analyse est un outil d'aide à la compréhension. Elle ne constitue pas un diagnostic et ne remplace pas l'avis d'un professionnel de santé. Seul votre médecin peut interpréter ces résultats en tenant compte de votre situation globale.
          </div>

          {/* Légende des couleurs : l'écran utilise des pastilles neutres → une légende
              est nécessaire pour interpréter les 3 statuts. */}
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-600 dark:text-gray-400">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: RESULT_COLORS.inRange.hex }} />
              Dans l'intervalle indiqué
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: RESULT_COLORS.outOfRange.hex }} />
              Hors de l'intervalle indiqué
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: RESULT_COLORS.unreadable.hex }} />
              Norme non lisible
            </span>
          </div>
        </header>

        {!isPremium ? (
          /* ── SECTION PREMIUM (Si non abonné) ── */
          <Card className="p-12 text-center bg-gray-900 dark:bg-black text-white overflow-hidden relative">
            <div className="absolute top-0 right-0 p-8 opacity-10"><Crown className="w-64 h-64" /></div>
            <Crown className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-4">
              {isTrialExpired ? 'Votre essai est terminé' : 'Fonctionnalité Réservée'}
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              {isTrialExpired
                ? "Votre essai de 7 jours est terminé. Abonnez-vous pour continuer à analyser vos examens biologiques — vos analyses passées sont conservées."
                : "Accédez à l'interprétation automatique de vos examens biologiques et suivez l'évolution de vos marqueurs de santé."}
            </p>
            <Button onClick={() => navigate('/pricing')} className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-12 py-8 text-xl rounded-2xl transition-transform hover:scale-105">
              Devenir Membre Premium
            </Button>
          </Card>
        ) : (
          /* ── SECTION UTILISATEUR (Si Premium) ── */
          <div className="grid lg:grid-cols-12 gap-10">
            
            {/* Colonne Gauche : Notice et Action */}
            <div className="lg:col-span-5 space-y-8">
              
              {/* Notice d'utilisation */}
              <Card className="p-6 border-none bg-gray-50 dark:bg-gray-900 shadow-inner">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" /> Notice d'utilisation
                </h3>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center text-xs font-bold text-blue-600 dark:text-blue-200">1</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400"><b>Capture :</b> Prenez une photo bien nette de votre compte-rendu. Évitez les reflets sur le papier.</p>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center text-xs font-bold text-blue-600 dark:text-blue-200">2</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400"><b>Upload :</b> Glissez le fichier dans la zone ou cliquez pour l'importer.</p>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center text-xs font-bold text-blue-600 dark:text-blue-200">3</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400"><b>Analyse :</b> L'IA identifie les valeurs (Cholestérol, Fer, etc.) et les compare aux normes.</p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
                  <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-2">
                    <Lightbulb className="w-3 h-3 text-yellow-500" /> Astuces pour un bon scan
                  </h4>
                  <ul className="text-xs text-gray-500 space-y-2">
                    <li className="flex items-center gap-2"><Camera className="w-3 h-3" /> Lumière du jour recommandée</li>
                    <li className="flex items-center gap-2"><FileText className="w-3 h-3" /> Formats acceptés : JPG, PNG</li>
                  </ul>
                </div>
              </Card>

              {/* Zone Upload */}
              {!imagePreview ? (
                <div 
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
                  onClick={() => document.getElementById('input-blood').click()}
                  className={`group relative h-72 border-4 border-dashed rounded-[32px] transition-all flex flex-col items-center justify-center cursor-pointer 
                    ${dragActive ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200 dark:border-gray-800 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-900'}`}
                >
                  <input type="file" id="input-blood" hidden onChange={(e) => handleFile(e.target.files[0])} />
                  <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Upload className="w-10 h-10 text-blue-600" />
                  </div>
                  <p className="font-bold text-gray-900 dark:text-white">Déposer l'analyse ici</p>
                  <p className="text-sm text-gray-400 mt-1">ou cliquez pour parcourir</p>
                </div>
              ) : (
                <Card className="p-4 relative group">
                  <img src={imagePreview} alt="Preview" className="w-full h-auto rounded-2xl border dark:border-gray-800 shadow-sm" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl flex items-center justify-center">
                    <Button onClick={() => setImagePreview(null)} variant="destructive">Changer d'image</Button>
                  </div>
                </Card>
              )}

              {image && !analysis && (
                <Button 
                  onClick={analyzeImage} 
                  disabled={loading}
                  className="w-full py-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl shadow-xl shadow-blue-500/20 text-lg font-bold"
                >
                  {loading ? <Loader2 className="animate-spin mr-3" /> : <Sparkles className="mr-3" />}
                  Lancer l'interprétation IA
                </Button>
              )}
            </div>

            {/* Colonne Droite : Résultats */}
            <div className="lg:col-span-7">
              {loading ? (
                <div className="h-full flex flex-col items-center justify-center p-12 text-center space-y-6">
                  <div className="relative">
                    <div className="w-24 h-24 border-4 border-blue-100 dark:border-gray-800 rounded-full animate-pulse" />
                    <Loader2 className="w-10 h-10 text-blue-500 animate-spin absolute top-7 left-7" />
                  </div>
                  <h3 className="text-2xl font-bold">Analyse en cours...</h3>
                  <div className="max-w-xs space-y-2">
                    <p className="text-sm text-gray-400">L'IA déchiffre les abréviations médicales et prépare vos analogies.</p>
                  </div>
                </div>
              ) : analysis ? (
                <div className="space-y-6 animate-in fade-in slide-in-from-right-8 duration-700">
                  <Card className="p-8 border-none bg-white dark:bg-gray-900 shadow-xl rounded-[32px]">
                    <div className="flex items-center gap-3 pb-6 border-b dark:border-gray-800 mb-6">
                      <HeartPulse className="w-8 h-8 text-rose-500" />
                      <h2 className="text-3xl font-bold">Rapport Simplifié</h2>
                    </div>
                    
                    <div className="prose dark:prose-invert max-w-none">
                      {renderAnalysisContent}
                    </div>

                    <div className="mt-12 p-6 bg-indigo-50 dark:bg-indigo-950/30 rounded-3xl border border-indigo-100 dark:border-indigo-800 flex gap-5">
                      <div className="w-12 h-12 bg-indigo-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                        <ArrowRight className="text-white w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-bold text-indigo-900 dark:text-indigo-300">Prochaine étape</h4>
                        <p className="text-sm text-indigo-800/70 dark:text-indigo-400/70">Enregistrez ce rapport ou imprimez-le pour votre prochaine visite chez le médecin afin de lui poser les bonnes questions.</p>
                      </div>
                    </div>
                  </Card>
                </div>
              ) : (
                <div className="h-full min-h-[500px] border-2 border-dashed border-gray-100 dark:border-gray-800 rounded-[40px] flex flex-col items-center justify-center text-gray-300 dark:text-gray-700 p-12">
                   <FileImage className="w-20 h-20 mb-4 opacity-20" />
                   <p className="text-xl font-medium">En attente de document</p>
                   <p className="text-sm mt-2 max-w-xs text-center">Vos résultats apparaîtront ici une fois que vous aurez lancé l'analyse.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── SECTION BAS DE PAGE : ÉTHIQUE & SÉCURITÉ ── */}
        <footer className="grid md:grid-cols-3 gap-6 pt-12 border-t dark:border-gray-800">
          <div className="flex gap-4">
            <Lock className="w-5 h-5 text-gray-400" />
            <div>
              <h5 className="font-bold text-sm text-gray-900 dark:text-white">Anonymisation</h5>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Vos documents ne sont jamais stockés sur nos serveurs. Seuls les résultats de l'analyse sont conservés, chiffrés et protégés par une clé propre à votre compte.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <HeartPulse className="w-5 h-5 text-gray-400" />
            <div>
              <h5 className="font-bold text-sm text-gray-900 dark:text-white">Aide au Patient</h5>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Conçu pour réduire l'anxiété liée à l'attente des résultats.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <ShieldCheck className="w-5 h-5 text-gray-400" />
            <div>
              <h5 className="font-bold text-sm text-gray-900 dark:text-white">Transparence</h5>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">L'image est transmise de façon sécurisée à notre partenaire d'analyse IA puis immédiatement supprimée."</p>
            </div>
          </div>
        </footer>

      </div>
    </DashboardLayout>
  );
};

export default BloodTestAnalysis;